import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import { PDFExport } from "@progress/kendo-react-pdf";
import MontlyCounterSaleSummaryModel from "../../../models/reports/monthlycountersalesummary_reports";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";
import { API_END_POINT } from "../../../constants";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

const MonthlyCounterSaleSummaryReport: React.FC = () => {
  const [MonthlyCounterSaleSummaryList, setMontlyounterSaleSummaryList] =
    useState<MontlyCounterSaleSummaryModel[]>([]);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
  );
  const [toDate, setToDate] = useState<Date>(new Date());
  const pdfExportRef = useRef<PDFExport | null>(null);
  const exportExcel = useRef<ExcelExport | null>(null);
  const { data: companyDetails } = useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

  const getMonthlycounterSaleSumaryList = async () => {
    try {
      setMontlyounterSaleSummaryList([]);
      const formattedFromDate = dayjs(fromDate).format("MM");
      const formattedToDate = dayjs(toDate).format("YYYY");
      const getMontlyCounterSaleSummarytUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/OrderCollectionReports/GetMonthlyCounterSaleSummary/${formattedFromDate}/${formattedToDate}`;
      const response = await getDataWithToken<any>(
        getMontlyCounterSaleSummarytUrl
      );
      const montlyCounterSaleSummary = response?.data?.result || [];
      setMontlyounterSaleSummaryList(montlyCounterSaleSummary);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const exportToExcel = () => {
    if (!exportExcel.current) return;

    const options = exportExcel.current.workbookOptions();
    const sheet = options.sheets[0];

    const formattedDate = fromDate
      ? new Date(fromDate).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "long",
        })
      : "";

    const headingWithDate = `Monthly Counter Sale Summary Report - ${formattedDate}`;

    sheet.rows.unshift({
      cells: [
        {
          value: headingWithDate,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });

    sheet.rows.unshift({
      cells: [
        {
          value: companyName,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });

    exportExcel.current.save(options);
  };

  const exportToPDF = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.save();
    }
  };

  const hasRecords = MonthlyCounterSaleSummaryList.length > 0;

  return (
    <>
      <div
        className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
        style={{ minHeight: "500px" }}
      >
        <div className="top-sec-align row">
          <center>
            <h4 className="text-normal title-web mb-0 report-title-color">
              Monthly Counter Sale Summary
            </h4>
          </center>
        </div>
        <div className="fade_rule"></div>
        <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
          <div className="d-flex">
            <div className="me-2">
              <label>Select Month</label>
              <DatePicker
                showMonthYearPicker
                className="form-control ms-auto search-right-align calendarBg"
                selected={fromDate}
                dateFormat="MMMM yyyy"
                onChange={(date: Date) => {
                  setFromDate(date);
                  setToDate(date);
                }}
                placeholderText="Select Month & Year"
              />
            </div>
            <div>
              <label></label>
              <button
                className="btn-primary btn search-btn mt-4"
                onClick={() => {
                  getMonthlycounterSaleSumaryList();
                }}
                type="submit"
              >
                <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                  <path
                    id="Path_magnify"
                    data-name="Path / magnify"
                    d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                    transform="translate(-3 -3)"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>
            <div className="d-flex justify-content-end">
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportToExcel}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to Excel
              </button>
              <button
                title="Export PDF"
                className="export-button ms-2"
                onClick={exportToPDF}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>

        <LoadingIndicator isLoading={false} />

        <PDFExport
          ref={pdfExportRef}
          fileName="Monthly_Counter_Sale_Summary_Report.pdf"
          title="Monthly Counter Sale Summary"
        >
          <div style={{ padding: "10px", backgroundColor: "#fff" }}>
            <div style={{ textAlign: "center", marginBottom: "5px" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                {companyDetails?.[0]?.companyName || "Company Name"}
              </span>
            </div>
            <center>
              <h4
                style={{
                  margin: 0,
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  display: "inline",
                }}
              >
                Monthly Counter Sale Summary Report
              </h4>
              <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                {" "}
                -{" "}
              </span>
              <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                {fromDate &&
                  new Date(fromDate).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "long",
                  })}
              </span>
            </center>
          </div>

          <Grid
            data={MonthlyCounterSaleSummaryList}
            style={{ width: "100%", overflowY: "auto" }}
          >
            {/* <GridColumn
              width={"220px"}
              title="S.No"
              cell={(props) => {
                const index = props.dataItem
                  ? MonthlyCounterSaleSummaryList.indexOf(props.dataItem) + 1
                  : 0;
                return <td>{index}</td>;
              }}
            /> */}
            <GridColumn
             field="saleDate"
             title="Sale Date"
             width="240px" />


            <GridColumn
              field="agentSalesAmount"
              title="Agent Sale Amount"
              columnMenu={null}
              cell={(props) => (
                <td style={{ textAlign: "right" }}>
                  {props.dataItem.agentSalesAmount?.toFixed(2)}
                </td>
              )}
              footerCell={(props) => {
                const totalAgentSalesAmount = MonthlyCounterSaleSummaryList.reduce(
                  (acc, item) => acc + (item.agentSalesAmount || 0),
                  0
                );
                return (
                  <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
                    {totalAgentSalesAmount.toFixed(2)}
                  </td>
                );
              }}
            />
            <GridColumn
              field="cardSalesAmount"
              title="Card Sale Amount"
              columnMenu={null}
              cell={(props) => (
                <td style={{ textAlign: "right" }}>
                  {props.dataItem.cardSalesAmount?.toFixed(2)}
                </td>
              )}
              footerCell={(props) => {
                const totalCardSalesAmount = MonthlyCounterSaleSummaryList.reduce(
                  (acc, item) => acc + (item.cardSalesAmount || 0),
                  0
                );
                return (
                  <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
                    {totalCardSalesAmount.toFixed(2)}
                  </td>
                );
              }}
            />
            <GridColumn
              field="totalSalesAmount"
              title="Total Sale Amount"
              columnMenu={null}
              cell={(props) => (
                <td style={{ textAlign: "right" }}>
                  {props.dataItem.totalSalesAmount?.toFixed(2)}
                </td>
              )}
              footerCell={(props) => {
                const totalTotalSalesAmount = MonthlyCounterSaleSummaryList.reduce(
                  (acc, item) => acc + (item.totalSalesAmount || 0),
                  0
                );
                return (
                  <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
                    {totalTotalSalesAmount.toFixed(2)}
                  </td>
                );
              }}
            />
          </Grid>
        </PDFExport>

        <ExcelExport
          fileName="Monthly Counter Sale Summary_List"
          data={MonthlyCounterSaleSummaryList}
          ref={exportExcel}
        >


          <ExcelExportColumn 
          field="saleDate" 
          title="Sale Date" 
          width={100} />

          
          <ExcelExportColumn
            field="agentSalesAmount"
            title="Agent Sales Amount"
            footer={() =>
              MonthlyCounterSaleSummaryList.reduce(
                (acc, item) => acc + (item.agentSalesAmount || 0),
                0
              ).toFixed(2)
            }
          />
          <ExcelExportColumn
            field="cardSalesAmount"
            title="Card Sales Amount"
            footer={() =>
              MonthlyCounterSaleSummaryList.reduce(
                (acc, item) => acc + (item.cardSalesAmount || 0),
                0
              ).toFixed(2)
            }
          />
          <ExcelExportColumn
            field="totalSalesAmount"
            title="Total Sales Amount"
            footer={() =>
              MonthlyCounterSaleSummaryList.reduce(
                (acc, item) => acc + (item.totalSalesAmount || 0),
                0
              ).toFixed(2)
            }
          />
        </ExcelExport>
      </div>
    </>
  );
};

export default MonthlyCounterSaleSummaryReport;
